import { AlertCircle, ArrowRight } from 'lucide-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useQueryClient, useSuspenseQuery, useIsFetching } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { googleAdsAccountAtom, organizationDataAtom } from '@/store';
import {
  googleAdsAccountsQueryOptions,
  organizationsQueryOptions,
  useSetGoogleAdsCustomerIdMutationOptions,
} from '@/lib/query-options';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';
import { usePlatformConnections } from '@/hooks/usePlatformConnections';
import { PlatformConnectionBanner } from '@/components/campaign/platform-connection-banner';
import { useToast } from '@/hooks/useToast';
import type { CampaignData } from '@/lib/types';

interface CampaignReadOnlyBannerProps {
  campaign: CampaignData;
}

export function CampaignReadOnlyBanner({ campaign }: CampaignReadOnlyBannerProps) {
  const organizationData = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const setGoogleAdsAccount = useSetAtom(googleAdsAccountAtom);
  const setOrganizationData = useSetAtom(organizationDataAtom);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { switchOrganization } = useOrganizationInitialization();
  const { data: accounts = [] } = useSuspenseQuery(googleAdsAccountsQueryOptions());
  const { data: organizations = [] } = useSuspenseQuery(organizationsQueryOptions());
  const setGoogleAdsCustomerId = useSetGoogleAdsCustomerIdMutationOptions();
  // During an org/account switch the atoms flip before the campaign data refetches,
  // so the cross-org/cross-account conditions are transiently true against stale data —
  // which flashed this amber banner while the new org loaded (#5824). Mismatch branches
  // are suppressed below while these queries are in flight; a genuine cross-org view
  // re-satisfies the condition once fresh data lands and the banner shows steadily.
  // Call both hooks unconditionally: `useIsFetching` is a hook, so joining the
  // two calls with `||` short-circuits the second whenever the first is > 0,
  // changing the hook count between renders → "Rendered fewer hooks than
  // expected" crash when navigating into a campaign mid-fetch (#5824 follow-up).
  const fetchingCampaign = useIsFetching({ queryKey: ['campaign', campaign?.slug] });
  const fetchingCampaigns = useIsFetching({ queryKey: ['campaigns'] });
  const isRefetching = fetchingCampaign > 0 || fetchingCampaigns > 0;
  // Google Ads not connected (#5937) — mirrors the isGoogleDisconnected condition the detail
  // page folds into isReadOnly, so the banner and the read-only state can't diverge.
  const { googleConnected, googleBroken, isLoading: isLoadingConnections } = usePlatformConnections();

  // Archived campaign banner
  if (campaign?.is_deleted) {
    return (
      <div className="bg-gray-50 border-l-4 border-l-gray-400 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Archived Campaign</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              This campaign is archived and read-only. Use the campaign menu in <span className="font-medium">Overview</span> to restore it.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Google Ads not connected (#5937): takes precedence over the mismatch branches below —
  // switching org/account can't make the campaign editable while there is no connection at all.
  if (!isLoadingConnections && !googleConnected) {
    return <PlatformConnectionBanner platform="google" broken={googleBroken} />;
  }

  const isExternal = campaign?.origin === 'external';
  const isCrossOrg = !!(
    organizationData?.id &&
    campaign?.organization_id &&
    campaign.organization_id !== organizationData.id
  );
  const isCrossAccount =
    !isCrossOrg &&
    !!(
      googleAdsAccount?.googleads_account_id &&
      campaign?.googleads_account?.googleads_account_id &&
      campaign.googleads_account.googleads_account_id !== googleAdsAccount.googleads_account_id
    );

  if (!isExternal && !isCrossOrg && !isCrossAccount) {
    return null;
  }
  if ((isCrossOrg || isCrossAccount) && isRefetching) {
    return null;
  }

  const targetAccountCustomerId = campaign?.googleads_account?.googleads_account_id;
  const targetAccount = accounts.find(
    (a) => a.googleads_account_id === targetAccountCustomerId,
  );
  const targetOrg = organizations.find((o) => o.id === campaign?.organization_id);

  const handleSwitchAccount = async () => {
    if (!targetAccount) return;
    try {
      await setGoogleAdsCustomerId.mutateAsync({
        googleAdsAccountId: targetAccount.googleads_account_id,
        organizationId: organizationData?.id,
      });
      setGoogleAdsAccount(targetAccount);
      if (organizationData) {
        setOrganizationData({
          ...organizationData,
          membership: {
            ...organizationData.membership,
            last_selected_googleads_account_id: targetAccount.googleads_account_id,
            last_selected_googleads_account_name:
              targetAccount.name || targetAccount.googleads_account_id,
          },
        });
      }
      queryClient.invalidateQueries({ queryKey: ['conversion-actions'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.slug] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    } catch {
      toast({ description: 'Failed to switch Google Ads account', variant: 'destructive' });
    }
  };

  const handleSwitchOrg = async () => {
    if (!targetOrg) return;
    try {
      await switchOrganization(targetOrg);
      if (targetAccount) {
        await setGoogleAdsCustomerId.mutateAsync({
          googleAdsAccountId: targetAccount.googleads_account_id,
          organizationId: targetOrg.id,
        });
      }
      // Hard reload to re-initialize route/atom state cleanly after clearAllUserData
      // wipes everything (draft atoms, query cache) during switchOrganization.
      window.location.href = `/campaigns/google/${campaign.slug}`;
    } catch {
      toast({ description: 'Failed to switch organization', variant: 'destructive' });
    }
  };

  if (isExternal) {
    return (
      <div className="bg-amber-50 border-l-4 border-l-amber-400 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-gray-900">
            This campaign is managed in Google Ads and is read-only in Glitch.
          </p>
        </div>
      </div>
    );
  }

  if (isCrossOrg) {
    const orgName = targetOrg?.name || 'another organization';
    return (
      <div className="bg-amber-50 border-l-4 border-l-amber-400 rounded-lg p-4 mb-4">
        <div className="flex items-start gap-4">
          <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900">
                This campaign belongs to {orgName}
              </p>
              <p className="text-sm text-gray-700 mt-0.5">
                Switch organizations to edit it.
              </p>
            </div>
            {targetOrg && (
              <Button
                onClick={handleSwitchOrg}
                className="bg-[#290BD9] hover:bg-[#2309C5] text-white shrink-0"
              >
                Switch to {orgName}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const accountName =
    targetAccount?.name ||
    campaign?.googleads_account?.name ||
    'another Google Ads account';
  return (
    <div className="bg-amber-50 border-l-4 border-l-amber-400 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-4">
        <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-gray-900">
              This campaign belongs to {accountName}
            </p>
            <p className="text-sm text-gray-700 mt-0.5">
              Switch Google Ads accounts to edit it.
            </p>
          </div>
          {targetAccount && (
            <Button
              onClick={handleSwitchAccount}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white shrink-0"
            >
              Switch to {accountName}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
